/**
 * 🎨 CENTRALIZED APP THEME CONTROLLER
 * 
 * Theme is now controlled by API (background.theme from /vfx-config endpoint)
 * Falls back to THEME_NUMBER if API is not available or still loading
 * 
 * All themes are VFX-friendly (dark enough for particle effects)
 * and optimized for casino/kiosk UI with white text readability
 */

// ============================================
// 👇 FALLBACK THEME NUMBER (used when API is unavailable)
// ============================================
export const THEME_NUMBER = 11; // Fallback theme (1-11) — 11 = Cosmos (default)
// ============================================

/**
 * Theme Configuration
 * All themes use Tailwind gradient classes for performance
 */
interface ThemeConfig {
  id: number;
  name: string;
  description: string;
  gradient: string; // Tailwind CSS gradient classes
  vfxCompatible: 'excellent' | 'good' | 'fair';
  bestFor: string;
}

/**
 * 10 VFX-Friendly Background Themes
 * Dark, sophisticated, perfect for particle effects
 */
export const APP_THEMES: ThemeConfig[] = [
  {
    id: 1,
    name: 'Kiosk Blue',
    description: 'Radial blue gradient from #03C to #001233 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#0033CC_0%,_#001233_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'Default theme - works with all VFX'
  },
  {
    id: 2,
    name: 'Royal Orange',
    description: 'Radial gradient from #ED671F to #270202 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#ED671F_0%,_#270202_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'Gold/white particle effects'
  },
  {
    id: 3,
    name: 'Golden Olive',
    description: 'Radial gradient from #564606 to #181301 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#564606_0%,_#181301_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'Light colored VFX (gold, white, cyan)'
  },
  {
    id: 4,
    name: 'Deep Purple',
    description: 'Radial gradient from #7400CC to #280443 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#7400CC_0%,_#280443_100%)]',
    vfxCompatible: 'good',
    bestFor: 'Gold, white, or pink VFX'
  },
  {
    id: 5,
    name: 'Slate Gray',
    description: 'Radial gradient from #8F93A0 to #151515 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#8F93A0_0%,_#37383C_71.78%,_#151515_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'Gold, white, or cyan VFX'
  },
  {
    id: 6,
    name: 'Magenta Night',
    description: 'Radial gradient from #B100CC to #3A0143 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#B100CC_0%,_#3A0143_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'Any colored VFX - darkest theme'
  },
  {
    id: 7,
    name: 'Teal Green',
    description: 'Radial gradient from #00CC96 to #003729 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#00CC96_0%,_#003729_100%)]',
    vfxCompatible: 'good',
    bestFor: 'White, gold, or cyan VFX'
  },
  {
    id: 8,
    name: 'Cyan Depth',
    description: 'Radial gradient from #0DDAF1 to #012D32 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#0DDAF1_0%,_#012D32_100%)]',
    vfxCompatible: 'good',
    bestFor: 'White or cyan VFX'
  },
  {
    id: 9,
    name: 'Golden Yellow',
    description: 'Radial gradient from #FFED4A to #3D3702 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#FFED4A_0%,_#3D3702_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'White, gold, or cyan VFX'
  },
  {
    id: 10,
    name: 'Violet Storm',
    description: 'Radial gradient from #9317E6 to #370558 (Figma design)',
    gradient: 'bg-[radial-gradient(94.34%_58.96%_at_50%_50%,_#9317E6_0%,_#370558_100%)]',
    vfxCompatible: 'excellent',
    bestFor: 'Any colored VFX - neutral tone'
  },
  {
    id: 11,
    name: 'Cosmos',
    description: 'Cosmos Atoms design system: space gradient #050B22→#001233 + star-field + nebula glow',
    gradient: 'bg-cosmos', // defined in globals.css (layered star-field/comet/nebula backgrounds)
    vfxCompatible: 'excellent',
    bestFor: 'Default Cosmos theme - starfield/comet VFX, magenta/gold/cyan UI'
  }
];

/**
 * Get the currently selected theme
 * @param themeNumber - Optional theme number from API (1-10). Falls back to THEME_NUMBER if not provided.
 */
export const getSelectedTheme = (themeNumber?: number): ThemeConfig => {
  const effectiveThemeNumber = themeNumber ?? THEME_NUMBER;
  const theme = APP_THEMES.find(t => t.id === effectiveThemeNumber);

  if (!theme) {
    console.warn(`⚠️ Invalid theme number: ${effectiveThemeNumber}. Using default theme 1.`);
    return APP_THEMES[0]; // Default to theme 1
  }

  return theme;
};

/**
 * Get the gradient classes for the selected theme
 * @param themeNumber - Optional theme number from API (1-10). Falls back to THEME_NUMBER if not provided.
 */
export const getThemeGradient = (themeNumber?: number): string => {
  return getSelectedTheme(themeNumber).gradient;
};

/**
 * Get theme name
 */
export const getThemeName = (): string => {
  return getSelectedTheme().name;
};

/**
 * Get theme description
 */
export const getThemeDescription = (): string => {
  return getSelectedTheme().description;
};

/**
 * Log current theme info to console (useful for debugging)
 */
export const logCurrentTheme = (): void => {
  const theme = getSelectedTheme();
  console.log(`
🎨 Current App Theme: ${theme.name} (${THEME_NUMBER}/10)
📝 ${theme.description}
🎬 VFX Compatibility: ${theme.vfxCompatible}
💡 Best for: ${theme.bestFor}
🎨 Gradient: ${theme.gradient}
  `);
};

/**
 * Special themes for payment pages (not affected by THEME_NUMBER)
 */
export const PAYMENT_PAGE_THEME = 'bg-cosmos';

/**
 * Check if a path is a payment-related page
 */
export const isPaymentPage = (pathname: string): boolean => {
  return pathname.includes('/payment-checkout') ||
    pathname.includes('/payout-request');
};

// Auto-log theme on import (only in development)
if (process.env.NODE_ENV === 'development') {
  logCurrentTheme();
}

export default {
  THEME_NUMBER,
  APP_THEMES,
  getSelectedTheme,
  getThemeGradient,
  getThemeName,
  getThemeDescription,
  logCurrentTheme,
  PAYMENT_PAGE_THEME,
  isPaymentPage
};

